import { useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import apiService from "../services/api";
import { BrandMention, ContentItem } from "../types";

/** Shared prefix so page-level cache writes reach the batch entry. */
export const BRAND_MENTIONS_BATCH_QUERY_KEY = [
  "podcast-content-analysis-mentions",
] as const;

export type BatchMentionsQueryData = {
  mentionsByContentId: Record<string, BrandMention[]>;
};

export type BrandTab = "valid" | "invalid" | "all";

export interface AggregatedBrand {
  brandName: string;
  count: number;
  classification: string;
  aliases: string[];
  brandId?: string;
  logoUrl?: string;
  s3Key?: string;
  isValid: boolean;
}

/** Ensures API URLs use a plain id string (handles rare $oid-shaped payloads). */
export function contentIdForApi(id: ContentItem["_id"]): string {
  if (typeof id === "string") return id;
  if (id != null && typeof id === "object" && "$oid" in id) {
    return (id as { $oid: string }).$oid;
  }
  return String(id);
}

export function isValidMention(m: BrandMention): boolean {
  return m.feedback?.status !== "rejected" && m.classification === "paid";
}

export function isInvalidMention(m: BrandMention): boolean {
  return m.feedback?.status === "rejected" || m.classification !== "paid";
}

function mentionMatchesTab(m: BrandMention, tab: BrandTab): boolean {
  if (tab === "valid") return isValidMention(m);
  if (tab === "invalid") return isInvalidMention(m);
  return true;
}

function normalizeText(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function mentionMatchesSearch(m: BrandMention, query: string): boolean {
  const normalizedQuery = normalizeText(query);
  const lowerQuery = query.toLowerCase();
  return (
    m.brandName.toLowerCase().includes(lowerQuery) ||
    normalizeText(m.brandName).includes(normalizedQuery) ||
    (m.aliases?.some(
      (alias) =>
        alias.toLowerCase().includes(lowerQuery) ||
        normalizeText(alias).includes(normalizedQuery),
    ) ??
      false)
  );
}

export function usePageBrandMentions(contentItems: ContentItem[]) {
  const analyzedItems = useMemo(
    () =>
      contentItems.filter(
        (item) => item.isAnalyzed || (item.brandMentionsCount ?? 0) > 0,
      ),
    [contentItems],
  );

  const analyzedIds = useMemo(
    () => analyzedItems.map((item) => contentIdForApi(item._id)).filter(Boolean),
    [analyzedItems],
  );

  // One request for the whole page. Keyed by the page's ids so paging refetches.
  const query = useQuery({
    queryKey: [...BRAND_MENTIONS_BATCH_QUERY_KEY, analyzedIds.join(",")],
    queryFn: () => apiService.getBrandMentionsBatch(analyzedIds),
    enabled: analyzedIds.length > 0,
    retry: (_: number, error: { response?: { status?: number } }) =>
      error?.response?.status !== 404,
  });

  const mentionsByContentId = useMemo(() => {
    const map = new Map<string, BrandMention[]>();
    const grouped = query.data?.mentionsByContentId;
    analyzedIds.forEach((contentIdStr) => {
      map.set(contentIdStr, grouped?.[contentIdStr] ?? []);
    });
    return map;
  }, [analyzedIds, query.data]);

  const allMentions = useMemo(() => {
    const mentions: BrandMention[] = [];
    mentionsByContentId.forEach((list) => mentions.push(...list));
    return mentions;
  }, [mentionsByContentId]);

  const isLoading = query.isLoading;
  const isError = query.isError;

  const validBrandCount = useMemo(
    () =>
      new Set(
        allMentions.filter(isValidMention).map((m) => m.brandName),
      ).size,
    [allMentions],
  );

  const invalidBrandCount = useMemo(
    () =>
      new Set(
        allMentions.filter(isInvalidMention).map((m) => m.brandName),
      ).size,
    [allMentions],
  );

  const aggregateBrands = useCallback(
    (tab: BrandTab, searchQuery = ""): AggregatedBrand[] => {
      let filtered = allMentions.filter((m) => mentionMatchesTab(m, tab));

      if (searchQuery.trim()) {
        filtered = filtered.filter((m) => mentionMatchesSearch(m, searchQuery));
      }

      const acc: Record<string, AggregatedBrand> = {};
      for (const m of filtered) {
        const key = m.brandName;
        if (acc[key]) {
          acc[key].count += 1;
        } else {
          acc[key] = {
            brandName: key,
            count: 1,
            classification: m.classification,
            aliases: m.aliases || [],
            brandId: m.brandId,
            logoUrl: m.logoUrl,
            s3Key: m.s3Key,
            isValid: isValidMention(m),
          };
        }
      }

      return Object.values(acc).sort((a, b) => b.count - a.count);
    },
    [allMentions],
  );

  const getContentIdsForBrand = useCallback(
    (brandName: string, tab: BrandTab): string[] => {
      const ids: string[] = [];
      mentionsByContentId.forEach((mentions, contentId) => {
        const hasMatch = mentions.some(
          (m) => m.brandName === brandName && mentionMatchesTab(m, tab),
        );
        if (hasMatch) ids.push(contentId);
      });
      return ids;
    },
    [mentionsByContentId],
  );

  const contentHasBrand = useCallback(
    (contentId: string, brandName: string, tab: BrandTab): boolean => {
      const mentions =
        mentionsByContentId.get(contentIdForApi(contentId)) ?? [];
      return mentions.some(
        (m) => m.brandName === brandName && mentionMatchesTab(m, tab),
      );
    },
    [mentionsByContentId],
  );

  const getMentionsForContent = useCallback(
    (contentId: string, brandName?: string | null): BrandMention[] => {
      const mentions =
        mentionsByContentId.get(contentIdForApi(contentId)) ?? [];
      const filtered = brandName
        ? mentions.filter((m) => m.brandName === brandName)
        : mentions;
      return [...filtered].sort((a, b) => a.startTime - b.startTime);
    },
    [mentionsByContentId],
  );

  // Page-level now: mentions for every episode arrive in the same request.
  const getMentionsLoadingForContent = useCallback(
    (contentId: string): boolean => {
      if (!mentionsByContentId.has(contentIdForApi(contentId))) return false;
      return isLoading;
    },
    [mentionsByContentId, isLoading],
  );

  return {
    mentionsByContentId,
    allMentions,
    isLoading,
    isError,
    validBrandCount,
    invalidBrandCount,
    aggregateBrands,
    getContentIdsForBrand,
    contentHasBrand,
    getMentionsForContent,
    getMentionsLoadingForContent,
  };
}
